if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAddToCartBar extends HTMLElement {
      connectedCallback() {
        // Quick add renders a copy of the product section inside a modal; the bar belongs to the product page only.
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }

        // Moved out of the section so `position: fixed` stays relative to the viewport
        // whatever transforms the theme applies to ancestors.
        if (this.parentElement !== document.body) {
          document.querySelectorAll(`sticky-atc-bar[data-source-id="${this.dataset.sourceId}"]`).forEach((bar) => {
            if (bar !== this) bar.remove();
          });
          document.body.appendChild(this);
        }

        if (this.initialized) return;
        this.init();
      }

      disconnectedCallback() {
        // Appending to the body disconnects and reconnects this element; only tear down a real removal.
        Promise.resolve().then(() => {
          if (this.isConnected) return;

          this.sourceObserver?.disconnect();
          this.visibilityObserver?.disconnect();
          this.resizeObserver?.disconnect();
          this.initialized = false;

          if (!document.querySelector('sticky-atc-bar')) {
            document.body.classList.remove('sticky-atc-enabled');
            document.body.style.removeProperty('--sticky-atc-height');
          }
        });
      }

      init() {
        this.source = document.getElementById(this.dataset.sourceId);
        this.button = this.querySelector('.sticky-atc__button');
        this.label = this.querySelector('.sticky-atc__label');
        this.spinner = this.querySelector('.loading-overlay__spinner');

        if (!this.source || !this.button) {
          this.remove();
          return;
        }

        this.initialized = true;

        // The real button stays in the page for keyboard and screen reader users; this is a visual mirror.
        this.setAttribute('aria-hidden', 'true');
        this.button.setAttribute('tabindex', '-1');
        this.button.addEventListener('click', this.onClick.bind(this));

        this.syncFromSource();
        this.sourceObserver = new MutationObserver(this.syncFromSource.bind(this));
        this.sourceObserver.observe(this.source, {
          attributes: true,
          childList: true,
          characterData: true,
          subtree: true,
        });

        this.visibilityObserver = new IntersectionObserver(
          (entries) => this.classList.toggle('sticky-atc--visible', !entries[0].isIntersecting),
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.source);

        this.reserveSpace();
        this.resizeObserver = new ResizeObserver(this.reserveSpace.bind(this));
        this.resizeObserver.observe(this);
      }

      onClick(event) {
        event.preventDefault();
        // Delegate to the real submit button so product-form.js keeps owning the request and cart rendering.
        this.source.click();
      }

      syncFromSource() {
        const sourceLabel = this.source.querySelector('span');
        if (sourceLabel && this.label) this.label.textContent = sourceLabel.textContent.trim();

        this.button.disabled = this.source.hasAttribute('disabled');

        if (this.source.getAttribute('aria-disabled') === 'true') {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }

        this.button.classList.toggle('loading', this.source.classList.contains('loading'));

        const sourceSpinner = this.source.querySelector('.loading-overlay__spinner');
        if (sourceSpinner && this.spinner) {
          this.spinner.classList.toggle('hidden', sourceSpinner.classList.contains('hidden'));
        }
      }

      reserveSpace() {
        document.body.classList.add('sticky-atc-enabled');
        document.body.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
      }
    }
  );
}
